import React from "react";
import PropTypes from "prop-types";
import { Redirect } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import { useAuthState } from "../../app/AuthStateContext";
import SteamSignInButton from "../../auth/SteamSignInButton";
import { useTemplateTrack } from "../TemplateTrackingProvider";
import { getPublicSiteSx } from "../../public-site/publicSiteStyles";
import usePublicStats from "../../public-site/usePublicStats";

const HERO_BG =
  "url(https://ouro-public-assets.s3.eu-west-2.amazonaws.com/home/cs-agents.png)";

export default function MinimalHomePage({ catalog, templateReady = true }) {
  const theme = useTheme();
  const sx = React.useMemo(() => getPublicSiteSx(theme), [theme]);
  const { googleResponse, onFailure, isAuthenticated } = useAuthState();
  const { user } = catalog;
  const track = useTemplateTrack();
  const stats = usePublicStats();
  const [googleAnchor, setGoogleAnchor] = React.useState(null);

  React.useEffect(() => {
    if (!templateReady) return;
    track("homepage_view");
  }, [templateReady, track]);

  if (isAuthenticated && user?.email) {
    return <Redirect to="/lobby" />;
  }

  const verifiedTotal = stats?.verifiedTotal ?? null;
  const matchCount = stats?.matchCount ?? null;
  const regionCount = stats?.regions?.length ?? null;

  return (
    <Box sx={sx.pageRoot}>
      {/* Hero */}
      <Box
        sx={{
          position: "relative",
          minHeight: { xs: "100svh", md: "95vh" },
          display: "flex",
          alignItems: "center",
          overflow: "hidden",
          mx: "calc(-50vw + 50%)",
          px: "calc(50vw - 50%)",
          "&:after": {
            content: '""',
            position: "absolute",
            inset: 0,
            backgroundImage: HERO_BG,
            backgroundSize: "cover",
            backgroundPosition: "center top",
            opacity: 0.13,
            filter: "blur(0.5px)",
            maskImage:
              "linear-gradient(to bottom, black 0%, black 70%, transparent 100%)",
            WebkitMaskImage:
              "linear-gradient(to bottom, black 0%, black 70%, transparent 100%)",
            pointerEvents: "none",
            zIndex: 0,
          },
          "&:before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(255,255,255,0.015) 3px, rgba(255,255,255,0.015) 4px)",
            pointerEvents: "none",
            zIndex: 0,
          },
        }}
      >
        <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ maxWidth: { xs: "100%", md: "56ch" }, py: { xs: 10, md: 0 } }}>
            {/* Eyebrow */}
            <Typography
              sx={{
                mb: 2.5,
                display: { xs: "none", lg: "block" },
                color: "#E8A33D",
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: "0.72rem",
                fontWeight: 600,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
              }}
            >
              CS2 Matchmaking · Human Reviewed · Free to Start
            </Typography>

            {/* Headline */}
            <Typography component="h1" sx={sx.heroTitle}>
              Enjoy CS Again.
              <Box component="span" sx={sx.heroTitleAccent}>
                Even solo.
              </Box>
            </Typography>

            {/* Subtitle — maximum 15 words */}
            <Typography
              sx={{
                mt: 2.5,
                mb: 0,
                color: "rgba(255,255,255,0.72)",
                fontSize: { xs: "1.05rem", md: "1.2rem" },
                lineHeight: 1.55,
                maxWidth: "44ch",
              }}
            >
              Verified players. Server-side anti-cheat. Free to start.
            </Typography>

            {/* CTA cluster */}
            <Box sx={{ mt: 4, display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
              <SteamSignInButton
                sx={sx.steamButton}
                showAssurance={false}
                surface="hero"
              />
            </Box>

            {/* Google fallback */}
            <Box sx={{ mt: 2 }}>
              <Typography
                component="button"
                onClick={(e) => {
                  track("google_popover_opened", { surface: "hero" });
                  setGoogleAnchor(e.currentTarget);
                }}
                sx={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  color: "rgba(255,255,255,0.45)",
                  fontSize: "0.82rem",
                  textDecoration: "underline",
                  textUnderlineOffset: "3px",
                  "&:hover": { color: "rgba(255,255,255,0.7)" },
                }}
              >
                Trouble with Steam? Sign in with Google instead.
              </Typography>
              <Popover
                open={Boolean(googleAnchor)}
                anchorEl={googleAnchor}
                onClose={() => setGoogleAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    p: 2,
                    backgroundColor: "#1a1a1a",
                    border: "1px solid rgba(255,255,255,0.1)",
                  },
                }}
              >
                <Box onClick={() => track("sso_google_clicked", { provider: "google" })}>
                  <GoogleLogin
                    width={200}
                    size="medium"
                    onSuccess={googleResponse}
                    onError={onFailure}
                  />
                </Box>
              </Popover>
            </Box>

            {/* Assurance line */}
            <Box
              sx={{
                mt: 2.5,
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                color: "rgba(255,255,255,0.38)",
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 13 }} />
              <Typography sx={{ fontSize: "0.75rem" }}>
                Official Steam OpenID · read-only · no credit card required
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Trust strip */}
      {(verifiedTotal || matchCount || regionCount) ? (
        <Box
          sx={{
            borderTop: "1px solid rgba(255,255,255,0.08)",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            py: { xs: 3.5, md: 4 },
          }}
        >
          <Container maxWidth="xl">
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: { xs: 4, md: 0 },
                justifyContent: { xs: "flex-start", md: "space-around" },
                alignItems: "center",
              }}
            >
              {verifiedTotal != null && (
                <TrustStat
                  value={verifiedTotal.toLocaleString()}
                  label="verified players"
                />
              )}
              {regionCount != null && (
                <TrustStat value={`${regionCount}`} label="regions live" />
              )}
              {matchCount != null && (
                <TrustStat
                  value={matchCount.toLocaleString()}
                  label="matches played"
                />
              )}
            </Box>
          </Container>
        </Box>
      ) : null}
    </Box>
  );
}

function TrustStat({ value, label }) {
  return (
    <Box sx={{ textAlign: { xs: "left", md: "center" } }}>
      <Typography
        sx={{
          fontSize: { xs: "2rem", md: "2.4rem" },
          fontWeight: 800,
          color: "#E8A33D",
          lineHeight: 1,
          fontFamily: "'JetBrains Mono', monospace",
        }}
      >
        {value}
      </Typography>
      <Typography
        sx={{
          mt: 0.5,
          fontSize: "0.82rem",
          color: "rgba(255,255,255,0.5)",
          letterSpacing: "0.05em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

TrustStat.propTypes = {
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
};

MinimalHomePage.propTypes = {
  catalog: PropTypes.shape({
    user: PropTypes.object,
  }),
  templateReady: PropTypes.bool,
};
